/**
 * DataExportView — écran « Données » (groupe Administratif), extraction plate CSV.
 *
 * Demande JGS (2026-09-13), montrée au CD31 le 17/09 : une extraction en fichier plat
 * CSV (séparateur point-virgule, ouvrable directement dans Excel). Ce n'est pas une
 * simulation en carton : chaque carte télécharge un vrai export, scopé par org et par
 * unité de travail comme le reste de l'application (backend/routes/data_export_routes.mjs).
 *
 * Le catalogue des jeux exportables vient du SERVEUR (`dataExportAPI.getCatalog`) —
 * rien de codé en dur ici (CLAUDE.md, « DON'T hardcode lists in React ») : un jeu de
 * données ajouté côté backend apparaît sans toucher ce fichier.
 *
 * FORMAT — même carte d'en-tête blanche que les autres vues (PoolExceptionsView) :
 * icône + titre + sous-titre, puis les filtres communs (période, U.T.), puis une carte
 * par jeu de données avec un aperçu des 5 premières lignes avant de télécharger.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Download, Eye, X, AlertTriangle, SlidersHorizontal } from 'lucide-react';
import dataExportAPI from '../../api/dataExport.js';
import { workingSiteAPI } from '../../api/admin.js';
import SkeletonLoader from '../common/SkeletonLoader.jsx';
import CustomExportModal from './CustomExportModal.jsx';

/** `YYYY-MM-DD` local, pour préremplir un `<input type="date">`. */
function toDateInput(date) {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function defaultRange() {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  return { from: toDateInput(from), to: toDateInput(to) };
}

function PreviewModal({ t, dataset, preview, loading, error, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('dataExport.preview.title')}</h2>
            <p className="text-sm text-gray-500">{dataset?.label}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
            aria-label={t('dataExport.preview.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-auto p-4">
          {loading && <SkeletonLoader type="list" rows={5} />}
          {!loading && error && (
            <p className="text-sm text-red-600">{t('dataExport.errors.previewFailed', { message: error })}</p>
          )}
          {!loading && !error && preview && preview.rows.length === 0 && (
            <p className="text-sm text-gray-500">{t('dataExport.preview.empty')}</p>
          )}
          {!loading && !error && preview && preview.rows.length > 0 && (
            <>
              <table className="min-w-full text-sm border-collapse">
                <thead>
                  <tr>
                    {preview.columns.map((col) => (
                      <th key={col} className="text-left font-semibold text-gray-700 border-b border-gray-200 px-3 py-2 whitespace-nowrap">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row, idx) => (
                    <tr key={idx} className="odd:bg-gray-50">
                      {preview.columns.map((col) => (
                        <td key={col} className="px-3 py-2 border-b border-gray-100 whitespace-nowrap">
                          {row[col]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-gray-500 mt-3">
                {t('dataExport.preview.totalRows', { count: preview.total_rows ?? preview.rows.length })}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DatasetCard({ t, dataset, filters, onError }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const handlePreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await dataExportAPI.preview(dataset.key, filters);
      setPreviewData(res);
    } catch (err) {
      setPreviewError(err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await dataExportAPI.download(dataset.key, filters);
    } catch (err) {
      onError?.(err.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 flex flex-col gap-3">
        <div>
          <h3 className="font-semibold text-gray-900">{dataset.label}</h3>
          <p className="text-sm text-gray-500">{dataset.description}</p>
          <p className="text-xs text-gray-400 mt-1">{dataset.row_count_hint}</p>
        </div>
        <div className="flex gap-2 mt-auto">
          <button
            type="button"
            onClick={handlePreview}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <Eye className="w-4 h-4" />
            {t('dataExport.card.preview')}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            <Download className="w-4 h-4" />
            {downloading ? t('dataExport.downloading') : t('dataExport.card.download')}
          </button>
        </div>
      </div>
      {previewOpen && (
        <PreviewModal
          t={t}
          dataset={dataset}
          preview={previewData}
          loading={previewLoading}
          error={previewError}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </>
  );
}

function DataExportView() {
  const { t } = useTranslation();
  const [range, setRange] = useState(defaultRange);
  const [siteId, setSiteId] = useState('');
  const [downloadError, setDownloadError] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);

  const { data: catalogData, isLoading: catalogLoading, error: catalogError } = useQuery({
    queryKey: ['dataExportCatalog'],
    queryFn: dataExportAPI.getCatalog,
  });

  const { data: sitesData } = useQuery({
    queryKey: ['dataExportSites'],
    queryFn: workingSiteAPI.getAll,
  });

  const datasets = catalogData?.data || [];
  const sites = sitesData?.data || [];

  // Le serveur ne rend que les U.T. du périmètre du compte. Un responsable borné à
  // une seule U.T. n'a rien d'autre à choisir : « Toutes les U.T. » lui laisserait
  // croire qu'il extrait le département (JGS, 2026-09-13). Une direction garde le
  // choix, qui couvre alors ses propres U.T.
  const singleSite = sites.length === 1;
  const effectiveSiteId = singleSite ? String(sites[0].id) : siteId;

  const filters = useMemo(
    () => ({ from: range.from, to: range.to, siteId: effectiveSiteId || undefined }),
    [range, effectiveSiteId]
  );

  return (
    <div className="p-6 space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Download className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('dataExport.title')}</h1>
              <p className="text-gray-500 text-sm">{t('dataExport.subtitle')}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-4 mt-6">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('dataExport.filters.from')}</label>
            <input
              type="date"
              value={range.from}
              onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('dataExport.filters.to')}</label>
            <input
              type="date"
              value={range.to}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('dataExport.filters.site')}</label>
            <select
              value={effectiveSiteId}
              onChange={(e) => setSiteId(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[180px]"
            >
              {!singleSite && <option value="">{t('dataExport.filters.allSites')}</option>}
              {sites.map((s) => (
                <option key={s.id} value={s.id}>{s.site_name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {downloadError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{t('dataExport.errors.downloadFailed', { message: downloadError })}</p>
        </div>
      )}

      {catalogLoading && <SkeletonLoader type="list" rows={4} />}
      {!catalogLoading && catalogError && (
        <p className="text-sm text-red-600">{t('dataExport.errors.catalogFailed', { message: catalogError.message })}</p>
      )}

      {!catalogLoading && !catalogError && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div className="bg-indigo-50 rounded-lg shadow-sm border-2 border-indigo-200 p-5 flex flex-col gap-3">
            <div>
              <h3 className="font-semibold text-indigo-900 flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4 text-indigo-500" />
                {t('dataExport.custom.cardTitle')}
              </h3>
              <p className="text-sm text-indigo-700/80">{t('dataExport.custom.cardDescription')}</p>
            </div>
            <div className="flex gap-2 mt-auto">
              <button
                type="button"
                onClick={() => setCustomOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
              >
                <SlidersHorizontal className="w-4 h-4" />
                {t('dataExport.custom.cardAction')}
              </button>
            </div>
          </div>
          {datasets.map((dataset) => (
            <DatasetCard key={dataset.key} t={t} dataset={dataset} filters={filters} onError={setDownloadError} />
          ))}
        </div>
      )}

      {customOpen && (
        <CustomExportModal
          t={t}
          datasets={datasets}
          sites={sites}
          singleSite={singleSite}
          initialFilters={filters}
          onClose={() => setCustomOpen(false)}
        />
      )}
    </div>
  );
}

export default DataExportView;
